import { FC, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { DataTable, Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import { Role } from 'types';

import RedeliveryActivity from './redelivery-activity';
import { RedeliveryContext } from './redelivery-context';
import RedeliveryUnits from './redelivery-units';
import './redelivery.scss';

const ViewRedelivery: FC = () => {
  const navigate = useNavigate();
  const { newZonedDayJs } = useTimeZone();
  const { userProfile } = useAuth();

  const {
    customer,
    expirationDate,
    id,
    loading,
    location,
    notes,
    redeliveryDetails,
    redeliveryNumber,
    startDate,
    truckingCompanies
  } = useContext(RedeliveryContext);

  const navigateToEditRedelivery = useCallback(() => {
    if (!id) return;
    navigate(!location ? `/redeliveries/${id}/edit` : `/redeliveries/${id}/edit?location=${location.id}`);
  }, [id, location, navigate]);

  const isInternalUser = useMemo(() => {
    return (
      userProfile.role === Role.ADMIN ||
      userProfile.role === Role.BILLING_ADMIN ||
      userProfile.role === Role.SENIOR_BOOKING ||
      userProfile.role === Role.BOOKING
    );
  }, [userProfile.role]);

  const isCustomerUser = useMemo(() => {
    return (
      (userProfile.role === Role.ADMIN_CUSTOMER ||
        userProfile.role === Role.CUSTOMER ||
        userProfile.role === Role.EXTERNAL) &&
      userProfile.customers?.some(({ id }) => id === customer?.id)
    );
  }, [customer, userProfile]);

  /* prettier-ignore */
  const truckingCompanyColumns = useMemo(() => {
    return [{ field: 'scacCode', label: 'SCAC CODE', width: 80 }, { field: 'name', label: 'NAME', width: 80 }];
  }, []);

  const fetchTruckingCompanies = useCallback(async () => [...truckingCompanies.values()], [truckingCompanies]);

  return (
    <div className="page" id="view-redelivery">
      <ErrorDialog />
      <SuccessDialog />
      <div className="page-header">
        <h1>REDELIVERY #{redeliveryNumber || 'N/A'}</h1>
        {!redeliveryNumber && <span className="inactive">NOT FOUND</span>}
        <div className="grid-3" style={{ direction: 'rtl' }}>
          {(isInternalUser || isCustomerUser) && (
            <button className="btn-primary" data-cy="redelivery-edit-button" onClick={navigateToEditRedelivery}>
              EDIT
            </button>
          )}
          {isInternalUser && <RedeliveryActivity />}
          <button className="btn-back align-self-center justify-self-center" onClick={() => navigate(-1)}>
            BACK
            <span className="material-icons">arrow_back_ios</span>
          </button>
        </div>
      </div>
      <div className="page-content">
        <Loader loading={loading}>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>REDELIVERY DETAILS</h4>
            </div>
            <div className="card-body">
              <dl className="grid-details">
                <dt className="text-dark font-weight-regular">ACCOUNT</dt>
                <dd className="text-black">{customer?.name ?? 'N/A'}</dd>
                <dt className="text-dark font-weight-regular">LOCATION</dt>
                <dd className="text-black">{location?.name ?? 'N/A'}</dd>
                <dt className="text-dark font-weight-regular">START DATE</dt>
                <dd className="text-black">{!startDate ? 'N/A' : newZonedDayJs(startDate).format('l')}</dd>
                <dt className="text-dark font-weight-regular">EXPIRATION DATE</dt>
                <dd className="text-black">{!expirationDate ? 'N/A' : newZonedDayJs(expirationDate).format('l')}</dd>
                <dt className="text-dark font-weight-regular">NOTES</dt>
                <dd className="text-black">{notes ?? 'N/A'}</dd>
              </dl>
            </div>
          </div>
          <div className="card-primary">
            <div className="card-header">
              <h4>EQUIPMENT DETAILS</h4>
            </div>
            <div className="card-body d-flex flex-column g-3">
              {[...redeliveryDetails.values()].map(redeliveryDetail => (
                <dl className="grid-details" key={redeliveryDetail.id}>
                  <dt className="text-dark font-weight-regular">SIZE TYPE</dt>
                  <dd className="text-black" data-cy="redelivery-size-type">
                    {redeliveryDetail.sizeType ?? 'N/A'}
                  </dd>
                  <dt className="text-dark font-weight-regular">QUANTITY</dt>
                  <dd className="text-black" data-cy="redelivery-count">
                    {redeliveryDetail.count ?? 0}
                  </dd>
                  <dt className="text-dark font-weight-regular">ASSIGNED</dt>
                  <dd className="text-black" data-cy="redelivery-unit-count">
                    {redeliveryDetail.unitCount ?? 0}
                  </dd>
                  <dt className="text-dark font-weight-regular">RECEIVED</dt>
                  <dd className="text-black" data-cy="redelivery-received">
                    {redeliveryDetail.received ?? 0}
                  </dd>
                  <dt />
                  <dd />
                </dl>
              ))}
            </div>
          </div>
          <RedeliveryUnits />
          <div className="card-primary">
            <div className="card-header">
              <h4>CARRIER DETAILS</h4>
            </div>
            <div className="card-body" style={{ marginTop: -44 }}>
              <DataTable
                clientSidePagination
                columns={truckingCompanyColumns}
                fetchData={fetchTruckingCompanies}
                hideTableControls
                initialColumns={truckingCompanyColumns}
                initialPage={{ pageNum: 0, pageSize: 5 }}
                noRowsMessage={<i>ANY TRUCKING COMPANY</i>}
                pageSizeOptions={[5, 25, 50]}
                getRowActions={() => [<></>]}
              />
            </div>
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default ViewRedelivery;
